import React, { useState } from 'react';
import useSimulationStore from '../../store/simulationStore';

/**
 * Top-level controls overlay - appears at bottom center on mouse hover
 * Contains: Play/Pause, Speed, Scale, Reset
 */
const TopControlsOverlay: React.FC = () => {
    const [isHovered, setIsHovered] = useState(false);

    const isRunning = useSimulationStore(state => state.isRunning);
    const controls = useSimulationStore(state => state.controls);
    const startSimulation = useSimulationStore(state => state.startSimulation);
    const pauseSimulation = useSimulationStore(state => state.pauseSimulation);
    const resetSimulation = useSimulationStore(state => state.resetSimulation);
    const setControls = useSimulationStore(state => state.setControls);

    const handleSpeedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setControls({ simulationSpeed: parseFloat(e.target.value) });
    };

    const handleScaleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setControls({ particleScale: parseFloat(e.target.value) });
    };

    const handleQualityChange = (quality: 'high' | 'medium' | 'low') => {
        setControls({ physicsQuality: quality });
    };

    const handlePlayPause = () => {
        if (isRunning) {
            pauseSimulation();
        } else {
            startSimulation();
        }
    };

    const currentQuality = controls.physicsQuality || 'medium';

    return (
        <>
            {/* Hover trigger zone at bottom of screen */}
            <div
                style={triggerZoneStyle}
                onMouseEnter={() => setIsHovered(true)}
            />

            {/* Control bar */}
            <div
                style={{
                    ...controlBarStyle,
                    opacity: isHovered ? 1 : 0,
                    pointerEvents: isHovered ? 'auto' : 'none',
                }}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                {/* Play/Pause Button */}
                <button
                    onClick={handlePlayPause}
                    style={buttonStyle}
                    title={isRunning ? 'Pause' : 'Play'}
                >
                    {isRunning ? '⏸️' : '▶️'}
                </button>

                {/* Divider */}
                <div style={dividerStyle} />

                {/* Speed Control */}
                <div style={controlGroupStyle}>
                    <label style={labelStyle}>Speed</label>
                    <input
                        type="range"
                        min="0.1"
                        max="3"
                        step="0.1"
                        value={controls.simulationSpeed}
                        onChange={handleSpeedChange}
                        style={sliderStyle}
                    />
                    <span style={valueStyle}>{controls.simulationSpeed.toFixed(1)}x</span>
                </div>

                {/* Divider */}
                <div style={dividerStyle} />

                {/* Scale Control */}
                <div style={controlGroupStyle}>
                    <label style={labelStyle}>Scale</label>
                    <input
                        type="range"
                        min="0.5"
                        max="2"
                        step="0.1"
                        value={controls.particleScale}
                        onChange={handleScaleChange}
                        style={sliderStyle}
                    />
                    <span style={valueStyle}>{controls.particleScale.toFixed(1)}x</span>
                </div>

                {/* Divider */}
                <div style={dividerStyle} />

                {/* Quality Control */}
                <div style={controlGroupStyle}>
                    <label style={labelStyle}>Quality</label>
                    <div style={qualityButtonGroupStyle}>
                        <button
                            onClick={() => handleQualityChange('low')}
                            style={{
                                ...qualityButtonStyle,
                                backgroundColor: currentQuality === 'low' ? '#4fc3f7' : 'rgba(255, 255, 255, 0.1)',
                                color: currentQuality === 'low' ? '#000' : '#fff',
                            }}
                            title="Low (20 fps physics)"
                        >
                            L
                        </button>
                        <button
                            onClick={() => handleQualityChange('medium')}
                            style={{
                                ...qualityButtonStyle,
                                backgroundColor: currentQuality === 'medium' ? '#4fc3f7' : 'rgba(255, 255, 255, 0.1)',
                                color: currentQuality === 'medium' ? '#000' : '#fff',
                            }}
                            title="Medium (30 fps physics)"
                        >
                            M
                        </button>
                        <button
                            onClick={() => handleQualityChange('high')}
                            style={{
                                ...qualityButtonStyle,
                                backgroundColor: currentQuality === 'high' ? '#4fc3f7' : 'rgba(255, 255, 255, 0.1)',
                                color: currentQuality === 'high' ? '#000' : '#fff',
                            }}
                            title="High (60 fps physics)"
                        >
                            H
                        </button>
                    </div>
                </div>

                {/* Divider */}
                <div style={dividerStyle} />

                {/* Projector Mode Toggle */}
                <button
                    onClick={() => setControls({ projectorMode: !controls.projectorMode })}
                    style={{
                        ...buttonStyle,
                        backgroundColor: controls.projectorMode ? '#4fc3f7' : 'rgba(255, 255, 255, 0.1)',
                        color: controls.projectorMode ? '#000' : '#fff',
                    }}
                    title={controls.projectorMode ? 'Projector Mode ON' : 'Projector Mode OFF'}
                >
                    📽️
                </button>

                {/* Divider */}
                <div style={dividerStyle} />

                {/* Reset Button */}
                <button
                    onClick={resetSimulation}
                    style={buttonStyle}
                    title="Reset Simulation"
                >
                    🔄
                </button>
            </div>
        </>
    );
};

// Styles
const triggerZoneStyle: React.CSSProperties = {
    position: 'fixed',
    bottom: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    width: '500px',
    height: '40px',
    zIndex: 999,
};

const controlBarStyle: React.CSSProperties = {
    position: 'fixed',
    bottom: '20px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 20px',
    backgroundColor: 'rgba(30, 30, 40, 0.9)',
    backdropFilter: 'blur(10px)',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    transition: 'opacity 0.3s ease',
    zIndex: 1000,
};

const buttonStyle: React.CSSProperties = {
    background: 'rgba(255, 255, 255, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '8px',
    padding: '8px 12px',
    fontSize: '18px',
    cursor: 'pointer',
    transition: 'background 0.2s ease',
};

const controlGroupStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
};

const labelStyle: React.CSSProperties = {
    color: '#aaa',
    fontSize: '12px',
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
};

const sliderStyle: React.CSSProperties = {
    width: '80px',
    accentColor: '#4fc3f7',
};

const valueStyle: React.CSSProperties = {
    color: '#4fc3f7',
    fontSize: '12px',
    fontWeight: 'bold',
    minWidth: '35px',
    textAlign: 'right',
};

const dividerStyle: React.CSSProperties = {
    width: '1px',
    height: '24px',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
};

const qualityButtonGroupStyle: React.CSSProperties = {
    display: 'flex',
    gap: '4px',
};

const qualityButtonStyle: React.CSSProperties = {
    background: 'rgba(255, 255, 255, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '4px',
    padding: '4px 8px',
    fontSize: '11px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'background 0.2s ease',
};

export default TopControlsOverlay;
